import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell,
  PieChart, Pie, Legend, CartesianGrid,
} from "recharts";
import { Download, SlidersHorizontal, X } from "lucide-react";
import Layout from "../components/Layout";
import { api } from "../lib/api";

const STAGE_COLORS = [
  "#94A3B8", "#CBD5E1", "#64748B", "#6366F1", "#F59E0B",
  "#F43F5E", "#8B5CF6", "#F97316", "#EA580C", "#22C55E",
];

function downloadCsv(filename, rows) {
  if (!rows || rows.length === 0) return;
  const cols = Array.from(
    rows.reduce((acc, r) => {
      Object.keys(r).forEach((k) => acc.add(k));
      return acc;
    }, new Set())
  );
  const escape = (v) => {
    if (v === null || v === undefined) return "";
    if (typeof v === "object") return `"${JSON.stringify(v).replace(/"/g, '""')}"`;
    const s = String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const csv = [cols.join(","), ...rows.map((r) => cols.map((c) => escape(r[c])).join(","))].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const a = document.createElement("a");
  const date = new Date().toISOString().slice(0, 10);
  a.href = URL.createObjectURL(blob);
  a.download = `${filename}-${date}.csv`;
  a.click();
  setTimeout(() => URL.revokeObjectURL(a.href), 1000);
}

function fmtMonth(s) {
  if (!s) return s;
  try {
    const [y, m] = s.split("-");
    return new Date(Number(y), Number(m) - 1, 1).toLocaleString("default", { month: "short", year: "numeric" });
  } catch {
    return s;
  }
}

export default function Reports() {
  const [funnel, setFunnel] = useState([]);
  const [sources, setSources] = useState([]);
  const [board, setBoard] = useState([]);
  const [lost, setLost] = useState([]);
  const [loading, setLoading] = useState(true);
  const [movements, setMovements] = useState({ months: [], agents: [], stages: [], cells: [] });
  const [movLoading, setMovLoading] = useState(true);
  const [selectedAgent, setSelectedAgent] = useState("__all__");
  const [tis, setTis] = useState({ agents: [], stages: [], cells: [] });
  const [tisLoading, setTisLoading] = useState(true);
  const [r1, setR1] = useState({ months: [], agents: [], matrix: {}, totals: {} });
  const [r1b, setR1b] = useState({ months: [], agents: [], stages: [], monthly_matrix: {}, stage_totals: {}, tagging_applied: "All" });
  const [r1bTagging, setR1bTagging] = useState("All");
  const [r2, setR2] = useState({ rows: [] });
  const [r3, setR3] = useState({ overall: { avg_days: 0, sample_size: 0 }, agents: [] });
  const [r6, setR6] = useState({ rows: [] });
  const [xLoading, setXLoading] = useState(true);
  const [options, setOptions] = useState({
    lead_owners: [], categories: [], opportunity_types: [],
    lead_sources: [], created_months: [], last_activity_months: [], utm_sources: [],
  });
  const [filters, setFilters] = useState({
    lead_owner_ids: [], categories: [], opportunity_types: [],
    lead_sources: [], created_months: [], last_activity_months: [], utm_sources: [],
  });

  // Build query string from current filters (URLSearchParams handles array repeats)
  const queryString = useMemo(() => {
    const sp = new URLSearchParams();
    Object.entries(filters).forEach(([k, vals]) => {
      (vals || []).forEach((v) => sp.append(k, v));
    });
    return sp.toString();
  }, [filters]);

  useEffect(() => {
    // Filter options are static-ish; fetch once
    (async () => {
      try {
        const { data } = await api.get("/reports/filter-options");
        setOptions(data);
      } catch { /* ignore */ }
    })();
  }, []);

  useEffect(() => {
    setLoading(true);
    (async () => {
      try {
        const { data } = await api.get(`/reports/all${queryString ? `?${queryString}` : ""}`);
        setFunnel(data.funnel || []);
        setSources(data.sources || []);
        setBoard(data.leaderboard || []);
        setLost(data.lost_reasons || []);
      } finally {
        setLoading(false);
      }
    })();
  }, [queryString]);

  // Stage-movement monthly data (independent from the /reports/all filters —
  // uses its own agent selection via the dropdown below the chart).
  useEffect(() => {
    setMovLoading(true);
    (async () => {
      try {
        const { data } = await api.get("/reports/stage-movements-monthly");
        setMovements(data || { months: [], agents: [], stages: [], cells: [] });
      } finally {
        setMovLoading(false);
      }
    })();
  }, []);

  // Time-in-Stage heatmap data
  useEffect(() => {
    setTisLoading(true);
    (async () => {
      try {
        const { data } = await api.get("/reports/time-in-stage");
        setTis(data || { agents: [], stages: [], cells: [] });
      } finally {
        setTisLoading(false);
      }
    })();
  }, []);

  // Excel-style reports (1, 3, 6) — Report 1 fetched once (no filter), R1b refetched when tagging changes
  useEffect(() => {
    setXLoading(true);
    (async () => {
      try {
        const [a, b, c, d] = await Promise.allSettled([
          api.get("/reports/live-closures-by-agent-monthly"),
          api.get("/reports/avg-conversion-time-by-agent"),
          api.get("/reports/stage-conversion-by-creation-month"),
          api.get("/reports/qualified-leads-by-month"),
        ]);
        if (a.status === "fulfilled") setR1(a.value.data || { months: [], agents: [], matrix: {}, totals: {} });
        if (b.status === "fulfilled") setR3(b.value.data || { overall: { avg_days: 0, sample_size: 0 }, agents: [] });
        if (c.status === "fulfilled") setR6(c.value.data || { rows: [] });
        if (d.status === "fulfilled") setR2(d.value.data || { rows: [] });
      } finally {
        setXLoading(false);
      }
    })();
  }, []);

  // Report 1b — Current pipeline snapshot; refetches when tagging changes
  useEffect(() => {
    (async () => {
      const qs = r1bTagging && r1bTagging !== "All" ? `?tagging=${encodeURIComponent(r1bTagging)}` : "";
      const { data } = await api.get(`/reports/current-pipeline-by-agent${qs}`);
      setR1b(data || { months: [], agents: [], stages: [], monthly_matrix: {}, stage_totals: {}, tagging_applied: "All" });
    })();
  }, [r1bTagging]);

  // Build lookup: `${agent_id}__${stage}` → cell for O(1) table rendering
  const tisMap = useMemo(() => {
    const m = new Map();
    (tis.cells || []).forEach((c) => m.set(`${c.agent_id}__${c.stage}`, c));
    return m;
  }, [tis]);

  // Compute min/max across all avg_days for heatmap colour scaling
  const tisRange = useMemo(() => {
    const vals = (tis.cells || []).map((c) => c.avg_days).filter((x) => Number.isFinite(x));
    if (vals.length === 0) return { min: 0, max: 0 };
    return { min: Math.min(...vals), max: Math.max(...vals) };
  }, [tis]);

  // Heatmap colour: low = emerald (fast/good), high = rose (slow/bad).
  // Linear interpolation clamped between the min/max of the current dataset.
  const heatColor = (val) => {
    if (val === undefined || val === null || !Number.isFinite(val)) return "";
    const { min, max } = tisRange;
    if (max === min) return "bg-emerald-50 text-emerald-700";
    const t = (val - min) / (max - min);
    if (t < 0.2) return "bg-emerald-50 text-emerald-700";
    if (t < 0.4) return "bg-lime-50 text-lime-700";
    if (t < 0.6) return "bg-amber-50 text-amber-700";
    if (t < 0.8) return "bg-orange-50 text-orange-700";
    return "bg-rose-50 text-rose-700 font-semibold";
  };

  const movementChartData = useMemo(() => {
    // Pivot cells into per-month rows with one column per stage. Filter by
    // selected agent (or aggregate across all agents when __all__).
    const cells = movements.cells || [];
    const filtered = selectedAgent === "__all__"
      ? cells
      : cells.filter((c) => c.agent_id === selectedAgent);
    const byMonth = new Map();
    for (const c of filtered) {
      const row = byMonth.get(c.month) || { month: c.month };
      row[c.stage] = (row[c.stage] || 0) + c.count;
      byMonth.set(c.month, row);
    }
    // Ensure ordered rows and zero-fill missing stages so the tooltip is complete
    return (movements.months || []).map((m) => {
      const row = byMonth.get(m) || { month: m };
      (movements.stages || []).forEach((s) => { if (row[s] === undefined) row[s] = 0; });
      return { ...row, monthLabel: fmtMonth(m) };
    });
  }, [movements, selectedAgent]);

  const movementTotal = useMemo(
    () => movementChartData.reduce((acc, r) => acc + (movements.stages || []).reduce((a, s) => a + (r[s] || 0), 0), 0),
    [movementChartData, movements.stages]
  );

  const toggleFilter = (key, value) => {
    setFilters((prev) => {
      const cur = prev[key] || [];
      const next = cur.includes(value) ? cur.filter((v) => v !== value) : [...cur, value];
      return { ...prev, [key]: next };
    });
  };

  const clearAllFilters = () => {
    setFilters({
      lead_owner_ids: [], categories: [], opportunity_types: [],
      lead_sources: [], created_months: [], last_activity_months: [], utm_sources: [],
    });
  };

  const activeCount = Object.values(filters).reduce((acc, v) => acc + (v?.length || 0), 0);

  return (
    <Layout title="Reports & Analytics" breadcrumb={["Reports"]}>
      <FilterBar
        options={options}
        filters={filters}
        toggle={toggleFilter}
        clearAll={clearAllFilters}
        activeCount={activeCount}
      />

      <div className="mb-5 flex justify-end items-center gap-2">
        {loading && <span className="text-xs text-slate-500">Loading…</span>}
        <button
          data-testid="reports-download-all-btn"
          onClick={() => {
            const all = [
              ...funnel.map((r) => ({ report: "Funnel", ...r })),
              ...sources.map((r) => ({ report: "Source Performance", ...r })),
              ...board.map((r) => ({ report: "Leaderboard", ...r })),
              ...lost.map((r) => ({ report: "Lost Reasons", ...r })),
            ];
            downloadCsv("lms-all-reports", all);
          }}
          className="text-sm font-medium px-3 py-1.5 rounded-md bg-orange-500 hover:bg-orange-600 text-white inline-flex items-center gap-1.5"
        >
          <Download size={14} /> Download all (CSV)
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <Card title="Pipeline funnel" onDownload={() => downloadCsv("pipeline-funnel", funnel)} downloadTestId="download-funnel">
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={funnel}>
                <XAxis dataKey="stage" tick={{ fontSize: 10 }} interval={0} angle={-25} textAnchor="end" height={70} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip />
                <Bar dataKey="count" radius={[6, 6, 0, 0]}>
                  {funnel.map((row, i) => <Cell key={row.stage || `f-${i}`} fill={STAGE_COLORS[i % STAGE_COLORS.length]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card title="Lost reason mix" onDownload={() => downloadCsv("lost-reasons", lost)} downloadTestId="download-lost-reasons">
          <div className="h-72 grid place-items-center">
            {lost.length === 0 ? (
              <div className="text-sm text-slate-500">No lost leads in scope.</div>
            ) : (
              <div className="w-full h-full">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={lost} dataKey="count" nameKey="reason" outerRadius={90} label>
                      {lost.map((row, i) => <Cell key={row.reason || `l-${i}`} fill={STAGE_COLORS[i % STAGE_COLORS.length]} />)}
                    </Pie>
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        </Card>

        <Card title="Source performance" onDownload={() => downloadCsv("source-performance", sources)} downloadTestId="download-sources">
          <table className="w-full text-sm">
            <thead><tr>{["Source", "Total", "Live", "Conv %"].map((h) => <th key={h} className="text-left py-2 text-xs uppercase tracking-wider text-slate-500">{h}</th>)}</tr></thead>
            <tbody>
              {sources.map((r) => (
                <tr key={r.lead_source} className="border-t border-slate-100">
                  <td className="py-2 font-medium">{r.lead_source}</td>
                  <td className="py-2">{r.total}</td>
                  <td className="py-2 text-emerald-700">{r.live}</td>
                  <td className="py-2"><span className="text-orange-600 font-semibold">{r.conversion_rate}%</span></td>
                </tr>
              ))}
              {sources.length === 0 && <tr><td colSpan={4} className="text-center py-6 text-slate-500">No data</td></tr>}
            </tbody>
          </table>
        </Card>

        <Card title={`Pipeline leaderboard`} onDownload={() => downloadCsv("leaderboard", board)} downloadTestId="download-leaderboard" className="lg:col-span-2">
          <table className="w-full text-sm">
            <thead><tr>{["Owner", "Total", "Live", "Lost", "Conv %"].map((h) => <th key={h} className="text-left py-2 text-xs uppercase tracking-wider text-slate-500">{h}</th>)}</tr></thead>
            <tbody>
              {board.map((r) => (
                <tr key={r.owner} className="border-t border-slate-100">
                  <td className="py-2 font-medium">{r.owner}</td>
                  <td className="py-2">{r.total}</td>
                  <td className="py-2 text-emerald-700">{r.live}</td>
                  <td className="py-2 text-rose-600">{r.lost}</td>
                  <td className="py-2 text-orange-600 font-semibold">{r.conversion_rate}%</td>
                </tr>
              ))}
              {board.length === 0 && <tr><td colSpan={5} className="text-center py-6 text-slate-500">No data</td></tr>}
            </tbody>
          </table>
        </Card>

        <Card
          title="Stage movement — monthly"
          onDownload={() => downloadCsv("stage-movements-monthly", (movements.cells || []).filter(c => selectedAgent === "__all__" || c.agent_id === selectedAgent))}
          downloadTestId="download-stage-movements"
          className="lg:col-span-2"
        >
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <div className="flex items-center gap-2">
              <label className="text-xs font-medium text-slate-600">Agent:</label>
              <select
                data-testid="stage-movement-agent-select"
                value={selectedAgent}
                onChange={(e) => setSelectedAgent(e.target.value)}
                className="text-sm border border-slate-200 rounded-md px-2.5 py-1.5 bg-white min-w-[200px]"
              >
                <option value="__all__">All agents (sum)</option>
                {(movements.agents || []).map((a) => (
                  <option key={a.id} value={a.id}>{a.name}</option>
                ))}
              </select>
            </div>
            <div className="text-xs text-slate-500">
              {movLoading ? "Loading…" : `${movementTotal} stage movement${movementTotal === 1 ? "" : "s"} across ${(movementChartData.length || 0)} month${movementChartData.length === 1 ? "" : "s"}`}
            </div>
          </div>

          {movementChartData.length === 0 ? (
            <div className="text-center py-10 text-sm text-slate-500">
              No stage movements recorded yet.
            </div>
          ) : (
            <>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={movementChartData} margin={{ top: 8, right: 12, left: 0, bottom: 8 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                    <XAxis dataKey="monthLabel" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                    <Tooltip contentStyle={{ fontSize: 12 }} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    {(movements.stages || []).map((s, i) => (
                      <Bar
                        key={s}
                        dataKey={s}
                        stackId="a"
                        fill={STAGE_COLORS[i % STAGE_COLORS.length]}
                        radius={i === (movements.stages || []).length - 1 ? [6, 6, 0, 0] : [0, 0, 0, 0]}
                      />
                    ))}
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div className="mt-4 text-xs text-slate-500">
                Each bar = number of leads that <em>entered</em> a stage during that month.
                {selectedAgent === "__all__"
                  ? " Currently showing the sum across ALL agents."
                  : ` Filtered to agent's transitions only.`}
              </div>
            </>
          )}
        </Card>

        <Card
          title="Time in stage — by agent (avg days)"
          onDownload={() => downloadCsv("time-in-stage", tis.cells || [])}
          downloadTestId="download-time-in-stage"
          className="lg:col-span-2"
        >
          <div className="text-xs text-slate-500 mb-3">
            Avg days a lead spent in each stage before moving on. Attribution: the agent who transitioned the lead INTO that stage owns the duration. Only closed transitions counted (leads still sitting in their current stage are excluded).
          </div>

          {tisLoading ? (
            <div className="text-center py-10 text-sm text-slate-500">Loading…</div>
          ) : (tis.cells || []).length === 0 ? (
            <div className="text-center py-10 text-sm text-slate-500">
              No closed stage transitions to analyse yet.
            </div>
          ) : (
            <div className="overflow-x-auto -mx-2 px-2">
              <table className="w-full text-sm" data-testid="time-in-stage-table">
                <thead>
                  <tr>
                    <th className="sticky left-0 bg-white text-left py-2 pr-4 text-xs uppercase tracking-wider text-slate-500 font-medium">
                      Agent
                    </th>
                    {(tis.stages || []).map((s) => (
                      <th key={s} className="text-center py-2 px-3 text-xs uppercase tracking-wider text-slate-500 font-medium whitespace-nowrap">
                        {s}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(tis.agents || []).map((a) => (
                    <tr key={a.id} className="border-t border-slate-100">
                      <td className="sticky left-0 bg-white py-2 pr-4 font-medium text-slate-800 whitespace-nowrap">{a.name}</td>
                      {(tis.stages || []).map((s) => {
                        const cell = tisMap.get(`${a.id}__${s}`);
                        if (!cell) {
                          return (
                            <td key={s} className="text-center py-2 px-3 text-slate-300">—</td>
                          );
                        }
                        return (
                          <td
                            key={s}
                            data-testid={`tis-cell-${a.id}-${s}`}
                            className={`text-center py-2 px-3 rounded transition-colors ${heatColor(cell.avg_days)}`}
                            title={`Sample: ${cell.sample_size} · min ${cell.min_days}d · max ${cell.max_days}d`}
                          >
                            <div className="font-semibold tabular-nums">{cell.avg_days}d</div>
                            <div className="text-[10px] text-slate-500 mt-0.5">n={cell.sample_size}</div>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="mt-4 flex items-center gap-3 text-[11px] text-slate-500">
                <span className="font-medium">Colour scale:</span>
                <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded bg-emerald-100 border border-emerald-300"></span>fast</span>
                <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded bg-amber-100 border border-amber-300"></span>medium</span>
                <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded bg-rose-100 border border-rose-300"></span>slow</span>
                <span className="ml-2">· Hover any cell to see sample size and range.</span>
              </div>
            </div>
          )}
        </Card>

        {/* ── Report 1 (original): Monthly Live closures + cumulative funnel totals ─── */}
        <Card
          title="Report 1 · Agent × Monthly Live closures"
          onDownload={() => {
            const rows = (r1.agents || []).map((a) => ({
              agent: a.name,
              ...Object.fromEntries((r1.months || []).map((m) => [m, r1.matrix?.[a.id]?.[m] || 0])),
              total_live: r1.totals?.[a.id]?.reached_live || 0,
              reached_fu_plus: r1.totals?.[a.id]?.reached_fu_plus || 0,
              reached_interested_plus: r1.totals?.[a.id]?.reached_interested_plus || 0,
              reached_oib_plus: r1.totals?.[a.id]?.reached_oib_plus || 0,
              total_leads: r1.totals?.[a.id]?.total_leads || 0,
            }));
            downloadCsv("report1-live-closures-agent-monthly", rows);
          }}
          downloadTestId="download-r1"
          className="lg:col-span-2"
        >
          <div className="text-xs text-slate-500 mb-3">
            Count of leads that <strong>entered the Live stage</strong> in each month, by agent. Rightmost columns show funnel totals — how many of each agent&apos;s leads ever reached Follow-up+ / Interested+ / OIB+ / Live at any point.
          </div>
          {xLoading ? (
            <div className="text-center py-10 text-sm text-slate-500">Loading…</div>
          ) : (r1.agents || []).length === 0 ? (
            <div className="text-center py-10 text-sm text-slate-500">No agent data yet.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="report1-table">
                <thead>
                  <tr>
                    <th className="sticky left-0 bg-white text-left py-2 pr-4 text-xs uppercase tracking-wider text-slate-500 font-medium">Agent</th>
                    {(r1.months || []).map((m) => (
                      <th key={m} className="text-right py-2 px-3 text-xs uppercase tracking-wider text-slate-500 font-medium whitespace-nowrap">{fmtMonth(m)}</th>
                    ))}
                    <th className="text-right py-2 px-3 text-xs uppercase tracking-wider text-emerald-700 font-semibold whitespace-nowrap border-l border-slate-200">Reached Live</th>
                    <th className="text-right py-2 px-3 text-xs uppercase tracking-wider text-slate-500 font-medium whitespace-nowrap">FU+</th>
                    <th className="text-right py-2 px-3 text-xs uppercase tracking-wider text-slate-500 font-medium whitespace-nowrap">Int+</th>
                    <th className="text-right py-2 px-3 text-xs uppercase tracking-wider text-slate-500 font-medium whitespace-nowrap">OIB+</th>
                    <th className="text-right py-2 px-3 text-xs uppercase tracking-wider text-slate-500 font-medium whitespace-nowrap">Total leads</th>
                  </tr>
                </thead>
                <tbody>
                  {(r1.agents || []).map((a) => {
                    const t = r1.totals?.[a.id] || {};
                    const isReal = a.id && a.id !== "__unassigned__";
                    const ownerParam = isReal ? `&lead_owner_id=${encodeURIComponent(a.id)}` : "";
                    return (
                      <tr key={a.id} className="border-t border-slate-100">
                        <td className="sticky left-0 bg-white py-2 pr-4 font-medium text-slate-800 whitespace-nowrap">{a.name}</td>
                        {(r1.months || []).map((m) => {
                          const v = r1.matrix?.[a.id]?.[m] || 0;
                          return (
                            <td key={m} className="text-right py-2 px-3 tabular-nums">
                              {v ? (
                                <Link
                                  to={`/reports/live-closures/${encodeURIComponent(m)}${isReal ? `?agent=${encodeURIComponent(a.id)}` : ""}`}
                                  className="text-emerald-700 font-semibold hover:text-emerald-900 hover:underline"
                                  data-testid={`r1-live-cell-${a.id}-${m}`}
                                >
                                  {v}
                                </Link>
                              ) : (
                                <span className="text-slate-300">—</span>
                              )}
                            </td>
                          );
                        })}
                        <td className="text-right py-2 px-3 tabular-nums font-semibold border-l border-slate-200">
                          {t.reached_live ? (
                            <Link to={`/leads?reached_group=live${ownerParam}`} className="text-emerald-700 hover:text-emerald-900 hover:underline" data-testid={`r1-reached-live-${a.id}`}>{t.reached_live}</Link>
                          ) : <span className="text-slate-300">0</span>}
                        </td>
                        <td className="text-right py-2 px-3 tabular-nums text-slate-700">
                          {t.reached_fu_plus ? (
                            <Link to={`/leads?reached_group=fu_plus${ownerParam}`} className="text-slate-700 hover:text-orange-600 hover:underline" data-testid={`r1-fu-${a.id}`}>{t.reached_fu_plus}</Link>
                          ) : <span className="text-slate-300">0</span>}
                        </td>
                        <td className="text-right py-2 px-3 tabular-nums text-slate-700">
                          {t.reached_interested_plus ? (
                            <Link to={`/leads?reached_group=interested_plus${ownerParam}`} className="text-slate-700 hover:text-orange-600 hover:underline" data-testid={`r1-int-${a.id}`}>{t.reached_interested_plus}</Link>
                          ) : <span className="text-slate-300">0</span>}
                        </td>
                        <td className="text-right py-2 px-3 tabular-nums text-slate-700">
                          {t.reached_oib_plus ? (
                            <Link to={`/leads?reached_group=oib_plus${ownerParam}`} className="text-slate-700 hover:text-orange-600 hover:underline" data-testid={`r1-oib-${a.id}`}>{t.reached_oib_plus}</Link>
                          ) : <span className="text-slate-300">0</span>}
                        </td>
                        <td className="text-right py-2 px-3 tabular-nums text-slate-500">
                          {t.total_leads && isReal ? (
                            <Link to={`/leads?lead_owner_id=${encodeURIComponent(a.id)}`} className="text-slate-500 hover:text-orange-600 hover:underline" data-testid={`r1-total-${a.id}`}>{t.total_leads}</Link>
                          ) : (t.total_leads || 0)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  {(() => {
                    const monthTotals = Object.fromEntries((r1.months || []).map((m) => [
                      m, (r1.agents || []).reduce((acc, a) => acc + (r1.matrix?.[a.id]?.[m] || 0), 0),
                    ]));
                    const sumTotal = (k) => (r1.agents || []).reduce((acc, a) => acc + (r1.totals?.[a.id]?.[k] || 0), 0);
                    return (
                      <tr className="border-t-2 border-slate-300 bg-slate-50 font-semibold" data-testid="report1-totals-row">
                        <td className="sticky left-0 bg-slate-50 py-2 pr-4 uppercase text-xs tracking-wider text-slate-600">Total</td>
                        {(r1.months || []).map((m) => (
                          <td key={m} className="text-right py-2 px-3 tabular-nums text-slate-800">
                            {monthTotals[m] ? (
                              <Link to={`/reports/live-closures/${encodeURIComponent(m)}`} className="text-slate-800 hover:text-orange-600 hover:underline" data-testid={`r1-live-total-${m}`}>{monthTotals[m]}</Link>
                            ) : 0}
                          </td>
                        ))}
                        <td className="text-right py-2 px-3 tabular-nums text-emerald-700 border-l border-slate-200">
                          {sumTotal("reached_live") ? (
                            <Link to="/leads?reached_group=live" className="text-emerald-700 hover:text-emerald-900 hover:underline" data-testid="r1-total-reached-live">{sumTotal("reached_live")}</Link>
                          ) : 0}
                        </td>
                        <td className="text-right py-2 px-3 tabular-nums text-slate-800">
                          {sumTotal("reached_fu_plus") ? (
                            <Link to="/leads?reached_group=fu_plus" className="text-slate-800 hover:text-orange-600 hover:underline" data-testid="r1-total-fu">{sumTotal("reached_fu_plus")}</Link>
                          ) : 0}
                        </td>
                        <td className="text-right py-2 px-3 tabular-nums text-slate-800">
                          {sumTotal("reached_interested_plus") ? (
                            <Link to="/leads?reached_group=interested_plus" className="text-slate-800 hover:text-orange-600 hover:underline" data-testid="r1-total-int">{sumTotal("reached_interested_plus")}</Link>
                          ) : 0}
                        </td>
                        <td className="text-right py-2 px-3 tabular-nums text-slate-800">
                          {sumTotal("reached_oib_plus") ? (
                            <Link to="/leads?reached_group=oib_plus" className="text-slate-800 hover:text-orange-600 hover:underline" data-testid="r1-total-oib">{sumTotal("reached_oib_plus")}</Link>
                          ) : 0}
                        </td>
                        <td className="text-right py-2 px-3 tabular-nums text-slate-800">{sumTotal("total_leads")}</td>
                      </tr>
                    );
                  })()}
                </tfoot>
              </table>
            </div>
          )}
        </Card>

        {/* ── Report 1b (NEW): Current pipeline snapshot + tagging filter ─── */}
        <Card
          title="Report 1b · Current pipeline snapshot (with tagging filter)"
          onDownload={() => {
            const stagesForCsv = (r1b.stages || []).filter((s) => s !== "Live");
            const rows = (r1b.agents || []).map((a) => {
              const st = r1b.stage_totals?.[a.id] || {};
              const base = {
                agent: a.name,
                tagging: r1b.tagging_applied || "All",
                ...Object.fromEntries((r1b.months || []).map((m) => [`live_${m}`, r1b.monthly_matrix?.[a.id]?.[m] || 0])),
                current_live: st["Live"] || 0,
              };
              stagesForCsv.forEach((s) => { base[`current_${s}`] = st[s] || 0; });
              base.total_leads = st.total_leads || 0;
              return base;
            });
            downloadCsv("report1b-current-pipeline", rows);
          }}
          downloadTestId="download-r1b"
          className="lg:col-span-2"
        >
          <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
            <div className="text-xs text-slate-500 max-w-3xl">
              Live snapshot of who has how many leads in each stage RIGHT NOW (not cumulative). Monthly Live columns show first-Live-entry counts per month, same as Report 1. Tagging filter scopes both views.
            </div>
            <div className="flex items-center gap-2">
              <label className="text-xs font-medium text-slate-600">Tagging:</label>
              <select
                data-testid="r1b-tagging-select"
                value={r1bTagging}
                onChange={(e) => setR1bTagging(e.target.value)}
                className="text-sm border border-slate-200 rounded-md px-2.5 py-1.5 bg-white min-w-[110px]"
              >
                {["All", "Hot", "Warm", "Cold", "Lost"].map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>
          </div>
          {(r1b.agents || []).length === 0 ? (
            <div className="text-center py-10 text-sm text-slate-500">
              No agent data{r1bTagging !== "All" ? ` matches tagging = "${r1bTagging}"` : " yet"}.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="report1b-table">
                <thead>
                  <tr>
                    <th className="sticky left-0 bg-white text-left py-2 pr-4 text-xs uppercase tracking-wider text-slate-500 font-medium">Agent</th>
                    {(r1b.months || []).map((m) => (
                      <th key={m} className="text-right py-2 px-3 text-xs uppercase tracking-wider text-slate-500 font-medium whitespace-nowrap">Live {fmtMonth(m)}</th>
                    ))}
                    <th className="text-right py-2 px-3 text-xs uppercase tracking-wider text-emerald-700 font-semibold whitespace-nowrap border-l border-slate-200">In Live</th>
                    {(r1b.stages || []).filter((s) => s !== "Live").map((s) => (
                      <th key={s} className="text-right py-2 px-3 text-xs uppercase tracking-wider text-slate-500 font-medium whitespace-nowrap">In {s}</th>
                    ))}
                    <th className="text-right py-2 px-3 text-xs uppercase tracking-wider text-slate-600 font-semibold whitespace-nowrap border-l border-slate-200">Total Leads</th>
                  </tr>
                </thead>
                <tbody>
                  {(r1b.agents || []).map((a) => {
                    const st = r1b.stage_totals?.[a.id] || {};
                    return (
                      <tr key={a.id} className="border-t border-slate-100">
                        <td className="sticky left-0 bg-white py-2 pr-4 font-medium text-slate-800 whitespace-nowrap">{a.name}</td>
                        {(r1b.months || []).map((m) => {
                          const v = r1b.monthly_matrix?.[a.id]?.[m] || 0;
                          return (
                            <td key={m} className={`text-right py-2 px-3 tabular-nums ${v ? "text-slate-800" : "text-slate-300"}`}>{v || "—"}</td>
                          );
                        })}
                        <td className="text-right py-2 px-3 tabular-nums font-semibold text-emerald-700 border-l border-slate-200">
                          {st["Live"] || 0}
                        </td>
                        {(r1b.stages || []).filter((s) => s !== "Live").map((s) => {
                          const c = st[s] || 0;
                          return (
                            <td key={s} className={`text-right py-2 px-3 tabular-nums ${c ? "text-slate-700" : "text-slate-300"}`}>{c || "—"}</td>
                          );
                        })}
                        <td className="text-right py-2 px-3 tabular-nums font-semibold text-slate-700 border-l border-slate-200">{st.total_leads || 0}</td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  {(() => {
                    const monthTotals = Object.fromEntries((r1b.months || []).map((m) => [
                      m, (r1b.agents || []).reduce((acc, a) => acc + (r1b.monthly_matrix?.[a.id]?.[m] || 0), 0),
                    ]));
                    const stageTotals = Object.fromEntries((r1b.stages || []).map((s) => [
                      s, (r1b.agents || []).reduce((acc, a) => acc + (r1b.stage_totals?.[a.id]?.[s] || 0), 0),
                    ]));
                    const grandTotalLeads = (r1b.agents || []).reduce((acc, a) => acc + (r1b.stage_totals?.[a.id]?.total_leads || 0), 0);
                    return (
                      <tr className="border-t-2 border-slate-300 bg-slate-50 font-semibold" data-testid="report1b-totals-row">
                        <td className="sticky left-0 bg-slate-50 py-2 pr-4 uppercase text-xs tracking-wider text-slate-600">Total</td>
                        {(r1b.months || []).map((m) => (
                          <td key={m} className="text-right py-2 px-3 tabular-nums text-slate-800">{monthTotals[m] || 0}</td>
                        ))}
                        <td className="text-right py-2 px-3 tabular-nums text-emerald-700 border-l border-slate-200">{stageTotals["Live"] || 0}</td>
                        {(r1b.stages || []).filter((s) => s !== "Live").map((s) => (
                          <td key={s} className="text-right py-2 px-3 tabular-nums text-slate-800">{stageTotals[s] || 0}</td>
                        ))}
                        <td className="text-right py-2 px-3 tabular-nums text-slate-800 border-l border-slate-200">{grandTotalLeads}</td>
                      </tr>
                    );
                  })()}
                </tfoot>
              </table>
            </div>
          )}
        </Card>

        {/* ── Report 2 (NEW): Qualified leads by creation month ─────────── */}
        <Card
          title="Report 2 · Qualified leads by month"
          onDownload={() => downloadCsv("report2-qualified-leads-by-month", r2.rows || [])}
          downloadTestId="download-r2"
          className="lg:col-span-2"
        >
          <div className="text-xs text-slate-500 mb-3">
            <div><strong>Leads Created</strong> = leads whose <em>creation month</em> falls in that row.</div>
            <div><strong>Same-Month Qualified</strong> = leads created in that month AND first-qualified in the <em>same</em> month.</div>
            <div><strong>Any-Month Qualified</strong> = leads (from <em>any</em> creation month) that were first-qualified in that row&apos;s month. E.g. Jul-2026 row shows all leads whose first qualifying transition happened in July, no matter when they were created.</div>
            <div className="mt-1 text-[11px] text-emerald-700">
              Tip: click any <b>Same-Month</b> or <b>Any-Month Qualified</b> number to open a stage × agent bifurcation. Then click a stage number to jump straight to the filtered leads.
            </div>
            <div className="mt-1 text-[11px] text-slate-400">
              Qualified stages: Follow-up, Interested, Contact-in-Future, Agreement In-Process, Onboarding-in-Process, Live.
            </div>
          </div>
          {xLoading ? (
            <div className="text-center py-10 text-sm text-slate-500">Loading…</div>
          ) : (r2.rows || []).length === 0 ? (
            <div className="text-center py-10 text-sm text-slate-500">No leads created yet.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="report2-table">
                <thead>
                  <tr>
                    <th className="text-left py-2 pr-3 text-xs uppercase tracking-wider text-slate-500 font-medium">Month</th>
                    <th className="text-right py-2 px-3 text-xs uppercase tracking-wider text-slate-500 font-medium whitespace-nowrap">Leads Created</th>
                    <th className="text-right py-2 px-3 text-xs uppercase tracking-wider text-slate-500 font-medium whitespace-nowrap border-l border-slate-200">Same-Month Qualified</th>
                    <th className="text-right py-2 px-3 text-xs uppercase tracking-wider text-orange-600 font-semibold whitespace-nowrap">Same-Month %</th>
                    <th className="text-right py-2 px-3 text-xs uppercase tracking-wider text-emerald-700 font-semibold whitespace-nowrap border-l border-slate-200">Any-Month Qualified</th>
                  </tr>
                </thead>
                <tbody>
                  {(r2.rows || []).map((r) => (
                    <tr key={r.month} className="border-t border-slate-100" data-testid={`r2-row-${r.month}`}>
                      <td className="py-2 pr-3 font-medium text-slate-800">{fmtMonth(r.month)}</td>
                      <td className="py-2 px-3 text-right tabular-nums text-slate-800">{r.leads_created}</td>
                      <td className="py-2 px-3 text-right tabular-nums text-slate-700 border-l border-slate-200">
                        {r.same_month_qualified > 0 ? (
                          <Link
                            to={`/reports/qualified/${r.month}/same_month`}
                            className="text-slate-700 hover:text-orange-600 hover:underline font-medium"
                            data-testid={`r2-same-link-${r.month}`}
                          >
                            {r.same_month_qualified}
                          </Link>
                        ) : r.same_month_qualified}
                      </td>
                      <td className="py-2 px-3 text-right tabular-nums font-semibold text-orange-600">{r.same_month_pct}%</td>
                      <td className="py-2 px-3 text-right tabular-nums font-semibold text-emerald-700 border-l border-slate-200">
                        {r.any_month_qualified > 0 ? (
                          <Link
                            to={`/reports/qualified/${r.month}/any_month`}
                            className="text-emerald-700 hover:text-emerald-900 hover:underline"
                            data-testid={`r2-any-link-${r.month}`}
                          >
                            {r.any_month_qualified}
                          </Link>
                        ) : r.any_month_qualified}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  {(() => {
                    const totLeads = (r2.rows || []).reduce((acc, r) => acc + (r.leads_created || 0), 0);
                    const totSame  = (r2.rows || []).reduce((acc, r) => acc + (r.same_month_qualified || 0), 0);
                    const totAny   = (r2.rows || []).reduce((acc, r) => acc + (r.any_month_qualified || 0), 0);
                    const pct = (n, d) => (d ? Math.round((n / d) * 10000) / 100 : 0);
                    return (
                      <tr className="border-t-2 border-slate-300 bg-slate-50 font-semibold" data-testid="report2-totals-row">
                        <td className="py-2 pr-3 uppercase text-xs tracking-wider text-slate-600">Total</td>
                        <td className="py-2 px-3 text-right tabular-nums text-slate-800">{totLeads}</td>
                        <td className="py-2 px-3 text-right tabular-nums text-slate-800 border-l border-slate-200">{totSame}</td>
                        <td className="py-2 px-3 text-right tabular-nums text-orange-600">{pct(totSame, totLeads)}%</td>
                        <td className="py-2 px-3 text-right tabular-nums text-emerald-700 border-l border-slate-200">{totAny}</td>
                      </tr>
                    );
                  })()}
                </tfoot>
              </table>
            </div>
          )}
        </Card>

        {/* ── Excel-style Report 3: Avg conversion time by agent ─────────── */}
        <Card
          title="Report 3 · Avg conversion time by agent"
          onDownload={() => downloadCsv("report3-avg-conversion-by-agent", r3.agents || [])}
          downloadTestId="download-r3"
        >
          <div className="text-xs text-slate-500 mb-3">
            Average number of days from <strong>lead creation</strong> → <strong>entered Live</strong>, per agent. Only leads that actually went Live are counted. Sample size <code>n</code> shown next to each average.
          </div>
          {xLoading ? (
            <div className="text-center py-10 text-sm text-slate-500">Loading…</div>
          ) : (
            <>
              <div className="mb-4 flex items-baseline gap-2 pb-3 border-b border-slate-100">
                <span className="text-3xl font-bold text-slate-800 tabular-nums" data-testid="r3-overall-avg">{r3.overall?.avg_days || 0}</span>
                <span className="text-sm text-slate-500">days overall avg</span>
                <span className="text-xs text-slate-400 ml-auto">n = {r3.overall?.sample_size || 0} leads</span>
              </div>
              {(r3.agents || []).length === 0 ? (
                <div className="text-center py-6 text-sm text-slate-500">No Live-stage transitions yet.</div>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr>
                      <th className="text-left py-2 text-xs uppercase tracking-wider text-slate-500 font-medium">Agent</th>
                      <th className="text-right py-2 text-xs uppercase tracking-wider text-slate-500 font-medium">Avg days</th>
                      <th className="text-right py-2 text-xs uppercase tracking-wider text-slate-500 font-medium">n</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(r3.agents || []).map((a) => (
                      <tr key={a.id} className="border-t border-slate-100" data-testid={`r3-row-${a.id}`}>
                        <td className="py-2 font-medium text-slate-800">{a.name}</td>
                        <td className="py-2 text-right tabular-nums font-semibold text-slate-800">{a.avg_days}</td>
                        <td className="py-2 text-right tabular-nums text-slate-500 text-xs">{a.sample_size}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </>
          )}
        </Card>

        {/* ── Excel-style Report 6: Stage conversion % by creation month ── */}
        <Card
          title="Report 6 · Funnel conversion % by lead-creation month"
          onDownload={() => downloadCsv("report6-funnel-by-creation-month", r6.rows || [])}
          downloadTestId="download-r6"
          className="lg:col-span-2"
        >
          <div className="text-xs text-slate-500 mb-3">
            For each month a lead was created, what % ever reached Follow-up+ / Interested+ / OIB+ / Live? Also shows stage-to-stage conversion (FU+ → Int+, Int+ → Live).
          </div>
          {xLoading ? (
            <div className="text-center py-10 text-sm text-slate-500">Loading…</div>
          ) : (r6.rows || []).length === 0 ? (
            <div className="text-center py-10 text-sm text-slate-500">No leads created yet.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="report6-table">
                <thead>
                  <tr>
                    <th className="text-left py-2 pr-3 text-xs uppercase tracking-wider text-slate-500 font-medium">Created Month</th>
                    <th className="text-right py-2 px-3 text-xs uppercase tracking-wider text-slate-500 font-medium whitespace-nowrap">Leads Created</th>
                    <th className="text-right py-2 px-3 text-xs uppercase tracking-wider text-slate-500 font-medium whitespace-nowrap">FU+ %</th>
                    <th className="text-right py-2 px-3 text-xs uppercase tracking-wider text-slate-500 font-medium whitespace-nowrap">Int+ %</th>
                    <th className="text-right py-2 px-3 text-xs uppercase tracking-wider text-slate-500 font-medium whitespace-nowrap">OIB+ %</th>
                    <th className="text-right py-2 px-3 text-xs uppercase tracking-wider text-emerald-700 font-semibold whitespace-nowrap">Live %</th>
                    <th className="text-right py-2 px-3 text-xs uppercase tracking-wider text-slate-500 font-medium whitespace-nowrap border-l border-slate-200">FU+ → Int+</th>
                    <th className="text-right py-2 px-3 text-xs uppercase tracking-wider text-slate-500 font-medium whitespace-nowrap">Int+ → Live</th>
                  </tr>
                </thead>
                <tbody>
                  {(r6.rows || []).map((r) => (
                    <tr key={r.month} className="border-t border-slate-100" data-testid={`r6-row-${r.month}`}>
                      <td className="py-2 pr-3 font-medium text-slate-800">{fmtMonth(r.month)}</td>
                      <td className="py-2 px-3 text-right tabular-nums text-slate-800">{r.leads_created}</td>
                      <td className="py-2 px-3 text-right tabular-nums text-slate-700">
                        <span className="text-slate-400 text-xs mr-1">{r.reached_fu_plus}</span>{r.fu_plus_pct}%
                      </td>
                      <td className="py-2 px-3 text-right tabular-nums text-slate-700">
                        <span className="text-slate-400 text-xs mr-1">{r.reached_interested_plus}</span>{r.interested_plus_pct}%
                      </td>
                      <td className="py-2 px-3 text-right tabular-nums text-slate-700">
                        <span className="text-slate-400 text-xs mr-1">{r.reached_oib_plus}</span>{r.oib_plus_pct}%
                      </td>
                      <td className="py-2 px-3 text-right tabular-nums font-semibold text-emerald-700">
                        <span className="text-emerald-500 text-xs mr-1 opacity-70">{r.reached_live}</span>{r.live_pct}%
                      </td>
                      <td className="py-2 px-3 text-right tabular-nums text-orange-600 border-l border-slate-200">{r.fu_to_int_pct}%</td>
                      <td className="py-2 px-3 text-right tabular-nums text-orange-600">{r.int_to_live_pct}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>
    </Layout>
  );
}

function FilterBar({ options, filters, toggle, clearAll, activeCount }) {
  const [openKey, setOpenKey] = useState(null);
  const groups = [
    { key: "lead_owner_ids",       label: "Lead Owner",           items: (options.lead_owners || []).map((o) => ({ value: o.id, label: o.name })) },
    { key: "categories",           label: "Category",             items: (options.categories || []).map((c) => ({ value: c, label: c })) },
    { key: "opportunity_types",    label: "Interested Services",  items: (options.opportunity_types || []).map((o) => ({ value: o, label: o })) },
    { key: "lead_sources",         label: "Lead Source",          items: (options.lead_sources || []).map((s) => ({ value: s, label: s })) },
    { key: "utm_sources",          label: "UTM Source",           items: (options.utm_sources || []).map((s) => ({ value: s, label: s })) },
    { key: "created_months",       label: "Lead Creation Month",  items: (options.created_months || []).map((m) => ({ value: m, label: fmtMonth(m) })) },
    { key: "last_activity_months", label: "Last Activity Month",  items: (options.last_activity_months || []).map((m) => ({ value: m, label: fmtMonth(m) })) },
  ];

  return (
    <div className="mb-4 bg-white border border-slate-200 rounded-xl p-3 shadow-sm" data-testid="reports-filter-bar">
      <div className="flex flex-wrap items-center gap-2">
        <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 inline-flex items-center gap-1.5 mr-1">
          <SlidersHorizontal size={12} /> Filters
        </div>
        {groups.map((g) => {
          const selected = filters[g.key] || [];
          const isOpen = openKey === g.key;
          return (
            <div key={g.key} className="relative">
              <button
                data-testid={`filter-trigger-${g.key}`}
                onClick={() => setOpenKey(isOpen ? null : g.key)}
                className={`text-xs font-medium px-3 py-1.5 rounded-full border inline-flex items-center gap-1.5 ${selected.length > 0 ? "bg-orange-500 border-orange-500 text-white" : "bg-white border-slate-200 text-slate-700 hover:border-slate-300"}`}
              >
                {g.label}
                {selected.length > 0 && <span className="bg-white/30 rounded-full px-1.5 py-px text-[10px] font-bold">{selected.length}</span>}
              </button>
              {isOpen && (
                <FilterPopover
                  groupKey={g.key}
                  items={g.items}
                  selected={selected}
                  onToggle={(v) => toggle(g.key, v)}
                  onClose={() => setOpenKey(null)}
                  onClear={() => filters[g.key]?.forEach((v) => toggle(g.key, v))}
                />
              )}
            </div>
          );
        })}
        {activeCount > 0 && (
          <button
            data-testid="filter-clear-all"
            onClick={clearAll}
            className="ml-1 text-xs font-medium px-2 py-1 text-rose-600 hover:bg-rose-50 rounded-md inline-flex items-center gap-1"
          >
            <X size={11} /> Clear all ({activeCount})
          </button>
        )}
      </div>
    </div>
  );
}

function FilterPopover({ groupKey, items, selected, onToggle, onClose, onClear }) {
  const [search, setSearch] = useState("");
  const filtered = items.filter((it) => it.label?.toLowerCase().includes(search.toLowerCase()));
  return (
    <>
      <div className="fixed inset-0 z-30" onClick={onClose} />
      <div className="absolute z-40 mt-1 w-64 bg-white border border-slate-200 rounded-lg shadow-lg p-2" data-testid={`filter-popover-${groupKey}`}>
        <input
          autoFocus
          placeholder="Search…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full text-xs px-2 py-1.5 border border-slate-200 rounded mb-1.5"
        />
        <div className="max-h-60 overflow-y-auto">
          {filtered.length === 0 && <div className="text-xs text-slate-400 px-2 py-2">No options</div>}
          {filtered.map((it) => (
            <label key={it.value} className="flex items-center gap-2 px-2 py-1 rounded hover:bg-slate-50 cursor-pointer">
              <input
                type="checkbox"
                checked={selected.includes(it.value)}
                onChange={() => onToggle(it.value)}
                className="accent-orange-500"
              />
              <span className="text-xs text-slate-700 truncate">{it.label}</span>
            </label>
          ))}
        </div>
        {selected.length > 0 && (
          <div className="border-t border-slate-100 mt-1.5 pt-1.5 flex justify-end">
            <button onClick={onClear} className="text-[11px] text-rose-600 hover:underline">Clear</button>
          </div>
        )}
      </div>
    </>
  );
}

function Card({ title, children, className = "", onDownload, downloadTestId }) {
  return (
    <div className={`bg-white border border-slate-200 rounded-xl p-5 shadow-sm ${className}`}>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-base font-semibold text-slate-900">{title}</h3>
        {onDownload && (
          <button
            data-testid={downloadTestId}
            onClick={onDownload}
            className="text-xs font-medium text-slate-600 hover:text-orange-600 inline-flex items-center gap-1 px-2 py-1 rounded-md hover:bg-orange-50"
            title="Download as CSV"
          >
            <Download size={12} /> CSV
          </button>
        )}
      </div>
      {children}
    </div>
  );
}
